'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { updateCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import {
  fetchCountries,
  fetchGenders,
  uploadFile,
} from '@eliadmin/core/src/api/general/general-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  CollapsibleContent,
  CollapsibleTrigger,
} from '@eliadmin/core/src/components/ui/collapsible';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import type { InputProps } from '@eliadmin/core/src/components/ui/input';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { initCustomerStore, useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Collapsible } from '@radix-ui/react-collapsible';
import { useMutation, useQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useRouter } from 'next/navigation';
import { useMemo, useRef, useState } from 'react';
import type { Control, FieldPath } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { FiCheck, FiChevronDown } from 'react-icons/fi';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { Customer } from '@eliadmin/core/src/api/customers/customers-types';
import { CancelSubscriptionModal } from '@eliadmin/core/src/components/profile/cancel-subscription-modal';
import { ResponsiveDialogProvider } from '@eliadmin/core/src/components/ui/responsive-dialog';
import { Heading } from '../ui/heading';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '../../constants/routes';
import { GoArrowLeft } from 'react-icons/go';
import { Field } from '../form/labeled-field';

type ProfileEditFormValues = z.infer<ReturnType<typeof profileEditSchema>>;
export function ProfileEditForm() {
  const customer = useCustomerStore(s => s.me!.customer);
  const authToken = useCustomerStore(s => s.me!.authToken);

  const ref = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | undefined>(undefined);

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const { data: countries = { data: [] }, isLoading: isCountriesLoading } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const { data: genders = { data: [] }, isLoading: isGendersLoading } = useQuery({
    queryKey: [CacheKey.GENDERS, locale],
    queryFn: () => fetchGenders({ locale }),
    refetchOnWindowFocus: false,
  });

  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ProfileEditFormValues>({
    resolver: zodResolver(profileEditSchema(formValidationMessages)),
    defaultValues: {
      name: customer?.name ?? '',
      surname: customer?.surname ?? '',
      email: customer?.email ?? '',
      phone: customer?.phone ?? '',
      gender: customer?.gender ?? undefined,
      address: customer?.address ?? '',
      street_number: customer?.street_number ?? '',
      city: customer?.city ?? '',
      postcode: customer?.postcode ?? '',
      country: customer?.country ?? undefined,
      iban: customer?.iban ?? '',
      swift: customer?.swift ?? '',
      address_delivery: customer?.address_delivery ?? '',
      street_number_delivery: customer?.street_number_delivery ?? '',
      postcode_delivery: customer?.postcode_delivery ?? '',
      city_delivery: customer?.city_delivery ?? '',
      country_delivery: customer?.country_delivery ?? undefined,
      company: customer?.company ?? '',
      ico: customer?.ico ?? '',
      dic: customer?.dic ?? '',
      ic_dph: customer?.ic_dph ?? '',
    },
  });

  const [isOpenInvoiceAddress, setIsOpenInvoiceAddress] = useState(false);
  const [isOpenCompanyData, setIsOpenCompanyData] = useState(false);
  const [isOpenCancelSubscription, setIsOpenCancelSubscription] = useState(false);

  const { mutateAsync } = useMutation({
    mutationFn: updateCustomer,
  });

  const handleSubmit = form.handleSubmit(async data => {
    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await uploadFile({
          authToken,
          formData,
          locale,
        });
        data.image = res.data;
      } catch (e) {
        //TODO: handle error / create custom field for this error
        return;
      }
    }

    try {
      await mutateAsync({
        locale,
        data: data as any,
        authToken,
      }).then(res => {
        const typedRes = res as { data: Customer };
        typedRes &&
          initCustomerStore({
            authToken,
            customer: { ...typedRes?.data },
          });
        return typedRes;
      });

      router.refresh();
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }
    }
  });

  const handlePickFile = () => {
    ref.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      setFile(undefined);
      return;
    }

    setFile(file);
  };

  const fullName = `${customer.name} ${customer.surname}`;

  const isDesktop = useBreakpoint('sm');

  return (
    <Form {...form}>
      <div className="relative w-full">
        <Button
          asChild
          variant={'outline'}
          size={isDesktop ? 'default' : 'icon'}
          className="absolute left-0 top-0 z-20"
        >
          <Link prefetch={false} href={ROUTES.PROFILE()}>
            <GoArrowLeft size={20} />
            {isDesktop && <span>{t('text1026')}</span>}
          </Link>
        </Button>
        <form
          className="mx-auto flex w-full max-w-[588px] flex-col items-center justify-center gap-8 sm:gap-10 lg:gap-12"
          onSubmit={handleSubmit}
        >
          <div className="flex w-full flex-col items-center justify-center gap-2">
            <Heading
              title={fullName}
              className={{ container: 'items-center justify-center text-center' }}
            />
            <p className="text-center text-base font-bold text-primary sm:text-lg">
              {customer?.email}
            </p>
          </div>
          {/* personal data */}
          <div className="grid w-full grid-cols-12 gap-4 sm:gap-5 lg:gap-6">
            <SectionHeader title={t('text360')} />
            <Field
              required
              isDisabled
              control={form.control}
              name="email"
              type="email"
              label={t('text35')}
              placeholder={t('text35')}
              className={{ wrapper: 'col-span-12 w-full opacity-50 sm:col-span-6' }}
            />
            <FormField
              control={form.control}
              name={'phone'}
              render={({ field }) => (
                <FormItem className="relative col-span-12 max-h-[65px] min-h-[65px] w-full rounded-none p-[1px] sm:col-span-6">
                  <FormControl>
                    <div className="relative flex max-h-[65px] min-h-[65px] items-end justify-end rounded-none">
                      <FormLabel
                        htmlFor={'phone'}
                        className={cn(
                          'absolute -top-0 left-3 z-[5] m-0 flex h-6 items-center justify-center bg-white p-1 text-sm font-semibold tracking-tight text-black',
                        )}
                        isRequired={false}
                      >
                        {t('text1258')} *
                      </FormLabel>

                      <div
                        className={cn(
                          'h-[54px] w-full overflow-hidden border border-border bg-white',
                        )}
                      >
                        <PhoneInputField
                          placeholder={t('text1258')}
                          {...(field as any)}
                          variant="default"
                          className="h-[54px] w-full"
                          inputClassName="h-[54px] bg-white text-base"
                          inputStyle={{
                            height: '54px',
                            width: '100%',
                            border: 'none',
                            paddingLeft: '20px',
                            fontSize: '16px',
                            lineHeight: '20px',
                            borderRadius: 0,
                            backgroundColor: 'transparent',
                          }}
                          countrySelectorStyleProps={{
                            buttonClassName: 'bg-white',
                            buttonStyle: {
                              height: '54px',
                              width: '58px',
                              paddingLeft: '12px',
                              border: 'none',
                              borderRadius: 0,
                              backgroundColor: 'transparent',
                            },
                          }}
                        />
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Field
              required
              control={form.control}
              name="name"
              label={t('text33')}
              placeholder={t('text33')}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            />
            <Field
              required
              control={form.control}
              name="surname"
              label={t('text34')}
              placeholder={t('text34')}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            />
          </div>
          <div className="grid w-full grid-cols-12 gap-4 sm:gap-5 lg:gap-6">
            <SectionHeader title={t('text157')} />
            <Field
              control={form.control}
              name="address"
              label={t('text38')}
              placeholder={t('text38')}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
            />
            <Field
              control={form.control}
              name="street_number"
              label={t('text438')}
              placeholder={t('text438')}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
            />
            <Field
              control={form.control}
              name="postcode"
              label={t('text40')}
              placeholder={t('text40')}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
            />
            <Field
              control={form.control}
              name="city"
              label={t('text39')}
              placeholder={t('text39')}
              className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
            />
            <Field
              control={form.control}
              type="select"
              name="country.id"
              label={t('text519')}
              placeholder={t('text519')}
              isDisabled={isCountriesLoading}
              className={{ wrapper: 'col-span-12 w-full' }}
              options={countries.data.map(c => ({
                value: c.id,
                label: c.name,
              }))}
            />
          </div>

          <div className="col-span-12 flex w-full items-center justify-center">
            <Button
              type="submit"
              className="w-full bg-[#1EBB60] md:w-auto"
              isLoading={form.formState.isSubmitting}
            >
              {t('text2290')}
            </Button>
          </div>
        </form>
      </div>
    </Form>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <Heading
      title={title}
      tag="h4"
      className={{ container: 'col-span-12 w-full items-center justify-center text-center' }}
    />
  );
}

export const profileEditSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    phone: z.string().optional(),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    username: z.string().optional(),
    address: z.string().optional(),
    street_number: z.string().optional(),
    postcode: z.string().optional(),
    city: z.string().optional(),
    country: z
      .object({
        id: z.string().or(z.number()).optional(),
      })
      .optional(),
    company_buy: z.boolean().optional(),
    company: z.string().optional(),
    ico: z.string().optional(),
    dic: z.string().optional(),
    ic_dph: z.string().optional(),
    gender: z
      .object({
        id: z.string().or(z.number()).optional(),
      })
      .optional(),
    image: z
      .object({
        id: z.number(),
      })
      .optional(),
    address_temporary: z.string().optional(),
    street_number_temporary: z.string().optional(),
    postcode_temporary: z.string().optional(),
    city_temporary: z.string().optional(),
    country_temporary: z
      .object({
        id: z.string(),
      })
      .optional(),
    email_company: z.string().email().optional(),
    address_company: z.string().optional(),
    street_number_company: z.string().optional(),
    postcode_company: z.string().optional(),
    city_company: z.string().optional(),
    country_company: z
      .object({
        id: z.string(),
      })
      .optional(),
    address_delivery: z.string().optional(),
    street_number_delivery: z.string().optional(),
    postcode_delivery: z.string().optional(),
    city_delivery: z.string().optional(),
    country_delivery: z
      .object({
        id: z.string(),
      })
      .optional(),
    name_delivery: z.string().optional(),
    surname_delivery: z.string().optional(),
    company_delivery: z.string().optional(),
    iban: z.string().optional(),
    swift: z.string().optional(),
    instagram: z.string().optional(),
    facebook: z.string().optional(),
    national_id_number: z.string().optional(),
  });
